import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  DraggableDialog,
  DraggableDialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/draggable-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Search,
  FolderOpen,
  ChevronLeft,
  ChevronRight,
  X,
  Lock,
  Users,
  Globe,
  Building2,
  Calendar,
  GitBranch,
  Workflow,
  User,
  ShieldAlert,
  Crown,
  Filter,
  CheckCircle2,
  RotateCcw,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useAnnouncer } from '@/hooks/useAnnouncer';
import { getCachedUser } from '@/lib/auth';
import type { WorkflowListItem } from '@/hooks/useWorkflow';

interface WorkflowLoadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLoad: (workflowId: string) => Promise<void>;
  onListWorkflows: (options: { page?: number; limit?: number; q?: string }) => Promise<{
    workflows: WorkflowListItem[];
    total: number;
    page: number;
    totalPages: number;
  } | null>;
}

// Visibility config
const VISIBILITY_CONFIG = {
  private: { icon: Lock, label: 'Privado', color: 'text-orange-600', bg: 'bg-orange-50 border-orange-200' },
  team: { icon: Users, label: 'Equipo', color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' },
  organization: { icon: Globe, label: 'Organización', color: 'text-green-600', bg: 'bg-green-50 border-green-200' },
} as const;

// Status config
const STATUS_CONFIG = {
  draft: { label: 'Borrador', variant: 'secondary' as const },
  active: { label: 'Activo', variant: 'default' as const },
  archived: { label: 'Archivado', variant: 'outline' as const },
} as const;

// Ownership type config
type OwnershipType = 'mine' | 'team' | 'others';

const OWNERSHIP_CONFIG = {
  mine: {
    icon: Crown,
    label: 'Mío',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50 border-emerald-200',
    canAccess: true
  },
  team: {
    icon: Users,
    label: 'De mi equipo',
    color: 'text-blue-600',
    bg: 'bg-blue-50 border-blue-200',
    canAccess: true
  },
  others: {
    icon: ShieldAlert,
    label: 'De otros',
    color: 'text-red-600',
    bg: 'bg-red-50 border-red-200',
    canAccess: false
  },
} as const;

export function WorkflowLoadDialog({
  open,
  onOpenChange,
  onLoad,
  onListWorkflows,
}: WorkflowLoadDialogProps) {
  const [workflows, setWorkflows] = useState<WorkflowListItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  // Filter state
  const [filterOwnership, setFilterOwnership] = useState<OwnershipType | 'all'>('all');
  const [filterVisibility, setFilterVisibility] = useState<'all' | 'private' | 'team' | 'organization'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'draft' | 'active' | 'archived'>('all');
  const [onlyAccessible, setOnlyAccessible] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  // Date range filters
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [datePreset, setDatePreset] = useState<'all' | 'week' | 'month' | '3months' | 'year' | 'custom'>('all');

  // Focus management
  const searchInputRef = useRef<HTMLInputElement>(null);

  // useAnnouncer for accessibility
  const { announce } = useAnnouncer();

  // Get current user for ownership detection
  const currentUser = useMemo(() => getCachedUser(), [open]);

  // Get user's team IDs
  const userTeamIds = useMemo(() => {
    if (!currentUser?.teams) return new Set<string>();
    return new Set(currentUser.teams.map(t => t.teamId));
  }, [currentUser?.teams]);

  // Determine ownership type for a workflow
  const getOwnershipType = (workflow: WorkflowListItem): OwnershipType => {
    // Check if it's mine (owner matches current user)
    if (workflow.owner?.id && currentUser?.id && workflow.owner.id === currentUser.id) {
      return 'mine';
    }

    // Check if it's from my team
    if (workflow.team?.id && userTeamIds.has(workflow.team.id)) {
      return 'team';
    }

    // Check organization visibility - everyone can access
    if (workflow.visibility === 'organization') {
      return 'team'; // Treat as accessible
    }

    // Otherwise, it's from others
    return 'others';
  };

  // Date preset helper
  const applyDatePreset = (preset: typeof datePreset) => {
    setDatePreset(preset);
    const now = new Date();
    const today = now.toISOString().split('T')[0];

    switch (preset) {
      case 'all':
        setDateFrom('');
        setDateTo('');
        break;
      case 'week': {
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        setDateFrom(weekAgo.toISOString().split('T')[0]);
        setDateTo(today);
        break;
      }
      case 'month': {
        const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        setDateFrom(monthAgo.toISOString().split('T')[0]);
        setDateTo(today);
        break;
      }
      case '3months': {
        const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        setDateFrom(threeMonthsAgo.toISOString().split('T')[0]);
        setDateTo(today);
        break;
      }
      case 'year': {
        const yearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        setDateFrom(yearAgo.toISOString().split('T')[0]);
        setDateTo(today);
        break;
      }
      case 'custom':
        // Keep current dates, just switch to custom mode
        break;
    }
  };

  // Determine if user can access a workflow
  const canAccessWorkflow = (workflow: WorkflowListItem): boolean => {
    const ownershipType = getOwnershipType(workflow);

    // Can always access mine or team's workflows
    if (ownershipType === 'mine' || ownershipType === 'team') {
      return true;
    }

    // For others' workflows, check visibility
    // Organization visibility = accessible
    if (workflow.visibility === 'organization') {
      return true;
    }

    // Team visibility = accessible if in same team (already handled above)
    // Private visibility = not accessible
    return false;
  };

  // Filter workflows based on selected filters
  const filteredWorkflows = useMemo(() => {
    return workflows.filter(workflow => {
      // Filter by ownership
      if (filterOwnership !== 'all') {
        const ownership = getOwnershipType(workflow);
        if (ownership !== filterOwnership) return false;
      }

      // Filter by visibility
      if (filterVisibility !== 'all') {
        if (workflow.visibility !== filterVisibility) return false;
      }

      // Filter by status
      if (filterStatus !== 'all') {
        if (workflow.status !== filterStatus) return false;
      }

      // Filter only accessible
      if (onlyAccessible && !canAccessWorkflow(workflow)) {
        return false;
      }

      // Filter by date range (based on updatedAt)
      if (dateFrom || dateTo) {
        const workflowDate = workflow.updatedAt ? new Date(workflow.updatedAt) : null;
        if (!workflowDate) return false;

        if (dateFrom) {
          const fromDate = new Date(dateFrom);
          fromDate.setHours(0, 0, 0, 0);
          if (workflowDate < fromDate) return false;
        }

        if (dateTo) {
          const toDate = new Date(dateTo);
          toDate.setHours(23, 59, 59, 999);
          if (workflowDate > toDate) return false;
        }
      }

      return true;
    });
  }, [workflows, filterOwnership, filterVisibility, filterStatus, onlyAccessible, currentUser, userTeamIds, dateFrom, dateTo]);

  // Check if any filter is active
  const hasActiveFilters = filterOwnership !== 'all' || filterVisibility !== 'all' || filterStatus !== 'all' || onlyAccessible || dateFrom !== '' || dateTo !== '';

  // Count active filters for badge
  const activeFilterCount = [
    filterOwnership !== 'all',
    filterVisibility !== 'all',
    filterStatus !== 'all',
    onlyAccessible,
    dateFrom !== '' || dateTo !== ''
  ].filter(Boolean).length;

  // Reset all filters
  const resetFilters = () => {
    setFilterOwnership('all');
    setFilterVisibility('all');
    setFilterStatus('all');
    setOnlyAccessible(false);
    setDateFrom('');
    setDateTo('');
    setDatePreset('all');
  };

  // Load workflows when dialog opens or page changes
  useEffect(() => {
    if (open) {
      loadWorkflows();
    }
  }, [open, page, searchQuery]);

  const loadWorkflows = async () => {
    setIsLoading(true);
    try {
      const result = await onListWorkflows({
        page,
        limit,
        q: searchQuery || undefined,
      });

      if (result) {
        setWorkflows(result.workflows);
        setTotal(result.total);
        setTotalPages(result.totalPages);
      }
    } catch (error) {
      console.error('Error loading workflows:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadWorkflow = async (workflowId: string) => {
    const workflow = workflows.find(w => w.id === workflowId);

    // Check access before loading
    if (workflow && !canAccessWorkflow(workflow)) {
      announce('No tienes permisos para acceder a este flujo visual', 'assertive');
      return;
    }

    try {
      await onLoad(workflowId);
      announce(workflow ? `Flujo visual "${workflow.name}" cargado exitosamente` : 'Flujo visual cargado exitosamente', 'polite');
      onOpenChange(false);
    } catch (error) {
      console.error('Error loading workflow:', error);
      announce('Error al cargar el flujo visual', 'assertive');
    }
  };

  const handleSearch = () => {
    setPage(1); // Reset to first page on new search
    loadWorkflows();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatShortDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getInitials = (name?: string) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Auto-focus on search input when opening
  useEffect(() => {
    if (open && searchInputRef.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      // Escape - Close dialog
      if (e.key === 'Escape') {
        e.preventDefault();
        onOpenChange(false);
      }
    };

    if (open) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, onOpenChange]);

  const getVisibilityConfig = (visibility: string) => {
    return VISIBILITY_CONFIG[visibility as keyof typeof VISIBILITY_CONFIG] || VISIBILITY_CONFIG.private;
  };

  const getStatusConfig = (status: string) => {
    return STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.draft;
  };

  return (
    <DraggableDialog open={open} onOpenChange={onOpenChange}>
      <DraggableDialogContent className="flex flex-col sm:max-w-[800px] max-h-[85vh] p-0 gap-0">
        {/* Header - Consistent pattern */}
        <div className="px-6 py-4 border-b bg-background">
          <DialogHeader className="text-left">
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5" />
              Abrir Flujo Visual
            </DialogTitle>
            <DialogDescription>
              Selecciona un flujo visual existente para abrirlo en el canvas.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden px-6 py-4">
          {/* Search + Filter Toggle */}
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                placeholder="Buscar flujos visuales por nombre, código o descripción..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                className="pl-9"
              />
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={showFilters ? 'default' : 'outline'}
                  size="icon"
                  onClick={() => setShowFilters(!showFilters)}
                  className="relative"
                >
                  <Filter className="h-4 w-4" />
                  {hasActiveFilters && (
                    <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-primary border-2 border-background" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}</TooltipContent>
            </Tooltip>
            <Button variant="outline" onClick={handleSearch}>
              Buscar
            </Button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mb-4 p-3 border rounded-lg bg-muted/30 space-y-3">
              {/* Filter Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Filter className="h-4 w-4" />
                  Filtros
                  {hasActiveFilters && (
                    <Badge variant="secondary" className="text-xs">
                      {activeFilterCount} activos
                    </Badge>
                  )}
                </div>
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={resetFilters} className="h-7 text-xs">
                    <RotateCcw className="h-3 w-3 mr-1" />
                    Limpiar
                  </Button>
                )}
              </div>

              {/* Filter Row 1: Ownership */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Propiedad</Label>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { value: 'all' as const, label: 'Todos', icon: null },
                    { value: 'mine' as const, label: 'Míos', icon: Crown },
                    { value: 'team' as const, label: 'Mi equipo', icon: Users },
                    { value: 'others' as const, label: 'De otros', icon: ShieldAlert },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setFilterOwnership(opt.value)}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs transition-colors ${
                        filterOwnership === opt.value
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background border hover:bg-accent'
                      }`}
                    >
                      {opt.icon && <opt.icon className="h-3 w-3" />}
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Filter Row 2: Visibility */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Visibilidad</Label>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { value: 'all' as const, label: 'Todos', icon: null },
                    { value: 'private' as const, label: 'Privado', icon: Lock },
                    { value: 'team' as const, label: 'Equipo', icon: Users },
                    { value: 'organization' as const, label: 'Organización', icon: Globe },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setFilterVisibility(opt.value)}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs transition-colors ${
                        filterVisibility === opt.value
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background border hover:bg-accent'
                      }`}
                    >
                      {opt.icon && <opt.icon className="h-3 w-3" />}
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Filter Row 3: Status */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Estado</Label>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { value: 'all' as const, label: 'Todos' },
                    { value: 'draft' as const, label: 'Borrador' },
                    { value: 'active' as const, label: 'Activo' },
                    { value: 'archived' as const, label: 'Archivado' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setFilterStatus(opt.value)}
                      className={`px-2.5 py-1 rounded-md text-xs transition-colors ${
                        filterStatus === opt.value
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background border hover:bg-accent'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Filter Row 4: Date Range */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Rango de fechas (actualización)</Label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {[
                    { value: 'all' as const, label: 'Todos' },
                    { value: 'week' as const, label: 'Última semana' },
                    { value: 'month' as const, label: 'Último mes' },
                    { value: '3months' as const, label: 'Últimos 3 meses' },
                    { value: 'year' as const, label: 'Último año' },
                    { value: 'custom' as const, label: 'Personalizado' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => applyDatePreset(opt.value)}
                      className={`px-2.5 py-1 rounded-md text-xs transition-colors ${
                        datePreset === opt.value
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background border hover:bg-accent'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {/* Custom date inputs - show when preset is custom or dates are set */}
                {(datePreset === 'custom' || (datePreset !== 'all' && (dateFrom || dateTo))) && (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      <Input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => {
                          setDateFrom(e.target.value);
                          setDatePreset('custom');
                        }}
                        className="h-8 text-xs w-[130px]"
                        placeholder="Desde"
                      />
                    </div>
                    <span className="text-xs text-muted-foreground">hasta</span>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => {
                        setDateTo(e.target.value);
                        setDatePreset('custom');
                      }}
                      className="h-8 text-xs w-[130px]"
                      placeholder="Hasta"
                    />
                    {(dateFrom || dateTo) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setDateFrom('');
                          setDateTo('');
                          setDatePreset('all');
                        }}
                        className="h-8 px-2"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Filter Row 5: Only Accessible Toggle */}
              <div className="flex items-center justify-between pt-2 border-t">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <Label htmlFor="only-accessible" className="text-xs cursor-pointer">
                    Solo mostrar accesibles
                  </Label>
                </div>
                <Switch
                  id="only-accessible"
                  checked={onlyAccessible}
                  onCheckedChange={setOnlyAccessible}
                />
              </div>
            </div>
          )}

          {/* Quick Filter Chips (when filters hidden but active) */}
          {!showFilters && hasActiveFilters && (
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-xs text-muted-foreground">Filtros:</span>
              {filterOwnership !== 'all' && (
                <Badge variant="secondary" className="text-xs gap-1">
                  {OWNERSHIP_CONFIG[filterOwnership].label}
                  <button onClick={() => setFilterOwnership('all')} className="ml-1 hover:text-destructive">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {filterVisibility !== 'all' && (
                <Badge variant="secondary" className="text-xs gap-1">
                  {VISIBILITY_CONFIG[filterVisibility].label}
                  <button onClick={() => setFilterVisibility('all')} className="ml-1 hover:text-destructive">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {filterStatus !== 'all' && (
                <Badge variant="secondary" className="text-xs gap-1">
                  {STATUS_CONFIG[filterStatus].label}
                  <button onClick={() => setFilterStatus('all')} className="ml-1 hover:text-destructive">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {(dateFrom || dateTo) && (
                <Badge variant="secondary" className="text-xs gap-1">
                  <Calendar className="h-3 w-3" />
                  {dateFrom && dateTo
                    ? `${new Date(dateFrom).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })} - ${new Date(dateTo).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}`
                    : dateFrom
                    ? `Desde ${new Date(dateFrom).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}`
                    : `Hasta ${new Date(dateTo).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}`}
                  <button onClick={() => { setDateFrom(''); setDateTo(''); setDatePreset('all'); }} className="ml-1 hover:text-destructive">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {onlyAccessible && (
                <Badge variant="secondary" className="text-xs gap-1">
                  Solo accesibles
                  <button onClick={() => setOnlyAccessible(false)} className="ml-1 hover:text-destructive">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              <button onClick={resetFilters} className="text-xs text-muted-foreground hover:text-foreground underline">
                Limpiar todos
              </button>
            </div>
          )}

          {/* Workflows List */}
          <ScrollArea className={`pr-4 ${showFilters ? 'h-[220px]' : 'h-[400px]'}`}>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <p className="text-muted-foreground">Cargando...</p>
              </div>
            ) : filteredWorkflows.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <FolderOpen className="h-12 w-12 mb-4 opacity-50" />
                {hasActiveFilters ? (
                  <>
                    <p>No se encontraron flujos visuales con los filtros seleccionados</p>
                    <Button variant="link" size="sm" onClick={resetFilters} className="mt-2">
                      Limpiar filtros
                    </Button>
                  </>
                ) : (
                  <p>No se encontraron flujos visuales</p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredWorkflows.map((workflow) => {
                  const visibilityConfig = getVisibilityConfig(workflow.visibility);
                  const statusConfig = getStatusConfig(workflow.status);
                  const VisibilityIcon = visibilityConfig.icon;
                  const ownershipType = getOwnershipType(workflow);
                  const ownershipConfig = OWNERSHIP_CONFIG[ownershipType];
                  const OwnershipIcon = ownershipConfig.icon;
                  const hasAccess = canAccessWorkflow(workflow);

                  return (
                    <button
                      key={workflow.id}
                      onClick={() => hasAccess && handleLoadWorkflow(workflow.id)}
                      disabled={!hasAccess}
                      className={`w-full p-4 text-left border rounded-lg transition-all group ${
                        hasAccess
                          ? 'hover:bg-accent hover:border-primary/30 cursor-pointer'
                          : 'opacity-60 cursor-not-allowed bg-muted/30'
                      }`}
                    >
                      {/* Row 1: Name + Ownership + Status + Visibility */}
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Workflow className={`h-4 w-4 flex-shrink-0 ${hasAccess ? 'text-muted-foreground' : 'text-muted-foreground/50'}`} />
                            <h4 className={`font-semibold truncate transition-colors ${hasAccess ? 'group-hover:text-primary' : 'text-muted-foreground'}`}>
                              {workflow.name}
                            </h4>
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5 ml-6">
                            {workflow.description || 'Sin descripción'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {/* Ownership Badge - NUEVO */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className={`flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-medium ${ownershipConfig.bg}`}>
                                <OwnershipIcon className={`h-3 w-3 ${ownershipConfig.color}`} />
                                <span className={ownershipConfig.color}>{ownershipConfig.label}</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              {hasAccess
                                ? `Puedes acceder a este flujo visual (${ownershipConfig.label})`
                                : 'No tienes permisos para acceder a este flujo visual'}
                            </TooltipContent>
                          </Tooltip>
                          {/* Visibility Badge */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className={`flex items-center gap-1 px-2 py-1 rounded-md border text-xs ${visibilityConfig.bg}`}>
                                <VisibilityIcon className={`h-3 w-3 ${visibilityConfig.color}`} />
                                <span className={visibilityConfig.color}>{visibilityConfig.label}</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>Visibilidad: {visibilityConfig.label}</TooltipContent>
                          </Tooltip>
                          {/* Status Badge */}
                          <Badge variant={statusConfig.variant}>
                            {statusConfig.label}
                          </Badge>
                        </div>
                      </div>

                      {/* Row 2: Owner + Team + Business Unit */}
                      <div className="flex items-center gap-4 mb-2 ml-6">
                        {/* Owner with avatar */}
                        {workflow.owner ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                <Avatar className="h-5 w-5">
                                  <AvatarFallback className={`text-[10px] ${ownershipType === 'mine' ? 'bg-emerald-100 text-emerald-700' : 'bg-primary/10'}`}>
                                    {getInitials(workflow.owner.fullName || workflow.owner.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className={`truncate max-w-[120px] ${ownershipType === 'mine' ? 'font-medium text-emerald-700' : ''}`}>
                                  {ownershipType === 'mine' ? 'Tú' : (workflow.owner.fullName || workflow.owner.name || workflow.owner.username)}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="text-xs">
                                <p className="font-medium">Owner</p>
                                <p>{workflow.owner.fullName || workflow.owner.name}</p>
                                {workflow.owner.username && <p className="text-muted-foreground">@{workflow.owner.username}</p>}
                                {ownershipType === 'mine' && <p className="text-emerald-600 font-medium mt-1">Este es tu flujo visual</p>}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className="flex items-center gap-1.5 text-xs text-muted-foreground/50 italic">
                            <User className="h-3.5 w-3.5" />
                            Sin owner
                          </span>
                        )}

                        {/* Team */}
                        {workflow.team && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className={`flex items-center gap-1.5 text-xs ${ownershipType === 'team' ? 'text-blue-600 font-medium' : 'text-muted-foreground'}`}>
                                <Users className="h-3.5 w-3.5" />
                                <span className="truncate max-w-[100px]">
                                  {userTeamIds.has(workflow.team.id) ? `${workflow.team.name} (tu equipo)` : workflow.team.name}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="text-xs">
                                <p className="font-medium">Equipo</p>
                                <p>{workflow.team.name}</p>
                                {userTeamIds.has(workflow.team.id) && <p className="text-blue-600 font-medium mt-1">Perteneces a este equipo</p>}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        )}

                        {/* Business Unit */}
                        {workflow.businessUnit && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                <Building2 className="h-3.5 w-3.5" />
                                <span className="truncate max-w-[100px]">{workflow.businessUnit.name}</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="text-xs">
                                <p className="font-medium">Unidad de Negocio</p>
                                <p>{workflow.businessUnit.name}</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>

                      {/* Row 3: Metadata */}
                      <div className="flex items-center justify-between gap-4 text-xs text-muted-foreground ml-6">
                        <div className="flex items-center gap-4">
                          {/* Code */}
                          <span className="font-mono bg-muted px-1.5 py-0.5 rounded">{workflow.code}</span>
                          {/* Version */}
                          <span className="flex items-center gap-1">
                            <GitBranch className="h-3 w-3" />
                            v{workflow.version}
                          </span>
                          {/* Nodes */}
                          <span>{workflow.nodeCount} nodos</span>
                          {/* Edges */}
                          <span>{workflow.edgeCount} conexiones</span>
                        </div>

                        {/* Tags */}
                        {workflow.tags && workflow.tags.length > 0 && (
                          <div className="flex gap-1 flex-wrap">
                            {workflow.tags.slice(0, 2).map((tag, idx) => (
                              <Badge key={idx} variant="outline" className="text-[10px] px-1.5 py-0">
                                {tag.name}
                              </Badge>
                            ))}
                            {workflow.tags.length > 2 && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                +{workflow.tags.length - 2}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Row 4: Dates */}
                      <div className="flex items-center justify-between gap-4 text-[11px] text-muted-foreground/70 mt-2 pt-2 border-t border-dashed ml-6">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          <span>Actualizado: {formatDate(workflow.updatedAt)}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Footer - Consistent pattern */}
        <TooltipProvider>
          <div className="flex items-center justify-between gap-3 border-t bg-background px-6 py-4">
            {/* LEFT: Pagination info */}
            <div className="text-sm text-muted-foreground">
              {!isLoading && workflows.length > 0 ? (
                hasActiveFilters ? (
                  `${filteredWorkflows.length} de ${workflows.length} flujos visuales (filtrados)`
                ) : (
                  `Mostrando ${(page - 1) * limit + 1} a ${Math.min(page * limit, total)} de ${total}`
                )
              ) : (
                <span>&nbsp;</span>
              )}
            </div>

            {/* RIGHT: Pagination + Close */}
            <div className="flex items-center gap-2">
              {/* Pagination controls */}
              {!isLoading && workflows.length > 0 && totalPages > 1 && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm min-w-[100px] text-center">
                    Página {page} de {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <div className="w-px h-6 bg-border mx-2" />
                </>
              )}

              {/* Close button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => onOpenChange(false)}
                    aria-label="Cerrar"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Cerrar (Esc)</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </TooltipProvider>
      </DraggableDialogContent>
    </DraggableDialog>
  );
}
